import { useState, useEffect, useRef } from 'react';
import { useScreenBreakpoints } from '@/hooks/useScreenBreakpoints';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { getMediaUrl } from '@/lib/utils';
import { MediaRequestMetadata } from '@/lib/types';
import { Trash2 } from 'lucide-react';

interface MediaCarouselDialogProps {
  isOpen: boolean;
  onClose: () => void;
  media: {
    id: string,
    characterName?: string,
    mediaDescription?: string,
    mediaPath?: string,
    mediaType?: string,
    mrMetadata: MediaRequestMetadata
  }[];
  initialIndex?: number;
  onDelete?: (id: string) => void;
}

export function MediaCarouselDialog({ isOpen, onClose, media, initialIndex = 0, onDelete }: MediaCarouselDialogProps) {
  const { isMobile } = useScreenBreakpoints();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  useEffect(() => {
    if (!carouselApi) return;

    carouselApi.on('select', () => {
      setCurrentIndex(carouselApi.selectedScrollSnap())
    }) 
  }, [carouselApi])

  const handlePrevious = () => {
    carouselApi?.scrollPrev()
  };

  const handleNext = () => {
    carouselApi?.scrollNext()
  };

  /** Handle carousel keyboard navigation when the dialog is focused instead of the carousel */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!carouselRef.current?.contains(document.activeElement) && e.key === 'ArrowLeft') {
      handlePrevious();
    } else if (!carouselRef.current?.contains(document.activeElement) && e.key === 'ArrowRight') {
      handleNext();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (media.length === 0) return null;

  const currentMedia = media[currentIndex];
  const isVideo = (mediaItem: { mediaType?: string }) => mediaItem?.mediaType?.includes('video');
  const isAdjacent = (index: number) => (Math.abs(index - currentIndex) % media.length) <= 2;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex items-center justify-center max-w-none w-full h-full md:w-auto md:h-auto md:max-w-4xl p-0 bg-black/95"
        onKeyDown={handleKeyDown}
      >
        <div className="relative max-w-screen w-full h-full flex flex-col items-center justify-center">
          {/* Header with counter */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-4">
            <div className="text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              {currentIndex + 1} / {media.length}
            </div>
          </div>
          {/* Delete button */}
          {onDelete && currentMedia?.id && (
            <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2">
              <button
                type="button"
                className="flex items-center justify-center size-9 rounded-full bg-black/50 hover:bg-black/70"
                aria-label="Delete media"
                onClick={() => onDelete?.(currentMedia.id)}
              >
                <Trash2 className="size-5 text-white" />
              </button>
            </div>
          )}

          <DialogTitle className="sr-only">{currentMedia?.characterName ?? 'Gallery'}</DialogTitle>
          <DialogDescription className="sr-only">{currentMedia?.mediaDescription ?? 'Gallery Media'}</DialogDescription>

          {/* Main content */}
          <div className="max-w-full flex-1 flex items-center justify-center p-4">
            <Carousel
              opts={{ loop: true, startIndex: initialIndex }}
              setApi={setCarouselApi}
              className="w-full h-[90vh] max-h-[1216px] aspect-[13/19] flex items-center justify-center"
              ref={carouselRef}
            >
              <CarouselContent>
                {media.filter(item => item.mediaPath).map((item, index) => (
                  <CarouselItem key={item.id} className="flex items-center justify-center">
                    <div className="relative w-full h-full flex items-center justify-center">
                    {isAdjacent(index) && (
                      isVideo(item) ? (
                        <video
                          src={getMediaUrl(item.mediaPath)}
                          controls
                          autoPlay={index === currentIndex}
                          loop
                          muted
                          className="max-w-full max-h-[90vh] h-[608px] object-contain aspect-[13/19]"
                        />
                      ) : (
                        <img
                          src={getMediaUrl(item.mediaPath)}
                          alt={`${item.characterName ?? 'Generated media'}: ${item.mediaDescription}`}
                          className="max-w-full max-h-[90vh] object-contain aspect-[13/19]"
                        />
                      )
                    )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Navigation controls */}
              {media.length > 1 && (
                <>
                  <CarouselPrevious 
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:scale-110 text-white border-0"
                    onClick={handlePrevious}
                  />
                  <CarouselNext 
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:scale-110 text-white border-0"
                    onClick={handleNext}
                  />
                </>
              )}
            </Carousel>
          </div>

          {/* Mobile navigation dots */}
          {isMobile && media.length > 1 && media.length <= 10 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-4">
              {media.map((_, index) => (
                <button
                  key={index}
                  className={`size-3 rounded-full transition-colors ${
                    index === currentIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                  onClick={() => setCurrentIndex(index)}
                />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 